import { useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useUsers } from '../../hooks/useUsers';
import { UserList } from '../UserList';
import { UserForm } from '../UserForm';
import { InviteModal } from './InviteModal';
import { User, UserInput } from '../../types';
import toast from 'react-hot-toast';

export function CompanyUsers() {
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | undefined>();
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        type: 'danger' | 'info';
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'info'
    });

    const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'info' = 'info') => {
        setConfirmDialog({
            isOpen: true,
            title,
            message,
            onConfirm,
            type
        });
    };

    const pageSize = 20;

    // Authenticated fetch helper needed for update/delete inside component if not exposed by hook
    // But useUsers exposes deleteUser. Update is manual in UsersPage.
    const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
        const token = localStorage.getItem('auth_token');
        const tenantId = localStorage.getItem('selected_tenant_id');
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            (headers as Record<string, string>).Authorization = `Bearer ${token}`;
        }

        if (tenantId) {
            (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
        }

        return fetch(url, {
            ...options,
            headers,
        });
    };

    const { users, totalPages, loading, error, refresh, deleteUser, allowedRoles, tenants } = useUsers({
        search: search || undefined,
        page: currentPage,
        pageSize,
    });

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const handleUpdateUser = async (data: UserInput) => {
        if (!editingUser) return;

        try {
            const response = await authenticatedFetch(`/api/users/${editingUser.id}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error('Erro ao atualizar usuário');
            }

            toast.success('Usuário atualizado com sucesso!');
            setIsEditFormOpen(false);
            setEditingUser(undefined);
            refresh();
        } catch (err) {
            console.error('Erro ao atualizar usuário:', err);
            toast.error('Erro ao atualizar usuário');
        }
    };

    const handleDeleteUser = async (id: string) => {
        // If it's an invite, the message is different (handled by UserList tooltip mostly, but here too)
        const isInvite = id.startsWith('invite-');
        const title = isInvite ? 'Cancelar Convite' : 'Remover Usuário';
        const message = isInvite
            ? 'Tem certeza que deseja cancelar este convite?'
            : 'Tem certeza que deseja remover este usuário do workspace?';

        showConfirm(
            title,
            message,
            async () => {
                try {
                    await deleteUser(id);
                    toast.success(isInvite ? 'Convite cancelado!' : 'Usuário removido com sucesso!');
                } catch (err) {
                    toast.error('Erro ao excluir usuário');
                }
            },
            'danger'
        );
    };

    const handleEditUser = (user: User) => {
        setEditingUser(user);
        setIsEditFormOpen(true);
    };

    const handleCloseEditForm = () => {
        setIsEditFormOpen(false);
        setEditingUser(undefined);
    };

    return (
        <div className="animate-fadeIn">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-white/5 backdrop-blur-xl p-8 rounded-3xl border border-white/10 mb-8 relative overflow-hidden group shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl group-hover:bg-blue-600/20 transition-all duration-700" />

                <div className="relative z-10">
                    <h2 className="text-3xl font-bold text-white font-oswald tracking-tight mb-2 flex items-center gap-3 uppercase">
                        <span className="p-2.5 bg-blue-600/20 rounded-xl border border-blue-500/30 shadow-lg text-2xl">👥</span> 
                        Gerenciar Usuários
                    </h2>
                    <p className="text-gray-400 font-medium flex items-center gap-2 ml-1">
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        {users.length} {users.length === 1 ? 'usuário cadastrado' : 'usuários cadastrados'}
                    </p>
                </div>

                <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                    <div className="relative w-full sm:w-72 group/search">
                        <input
                            type="text"
                            placeholder="Buscar usuários..."
                            value={search}
                            onChange={handleSearchChange}
                            className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all font-medium text-white placeholder-gray-500 focus:outline-none focus:bg-white/10 shadow-inner"
                        />
                        <span className="absolute left-4 top-4.5 text-gray-500 group-focus-within/search:text-blue-500 transition-colors text-xl">🔍</span>
                    </div>

                    <button
                        onClick={() => setIsInviteModalOpen(true)}
                        className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-blue-900/40 transition-all duration-300 font-bold font-oswald tracking-widest uppercase flex items-center justify-center gap-3 transform hover:-translate-y-1 border border-blue-400/20 active:scale-95"
                    >
                        <span className="text-lg">✉️</span>
                        <span>CONVIDAR USUÁRIO</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-500/10 backdrop-blur-md border border-red-500/20 text-red-400 px-8 py-5 rounded-2xl shadow-xl animate-fadeIn flex items-center gap-4 mb-8">
                    <span className="text-xl">⚠️</span>
                    <span className="font-medium tracking-wide">{error}</span>
                </div>
            )}

            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden group/list transition-all duration-500 hover:border-white/20">
                <UserList
                    users={users}
                    loading={loading}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                />
            </div>

            {totalPages > 1 && (
                <div className="flex justify-center mt-10 space-x-3">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-12 h-12 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center ${page === currentPage
                                ? 'bg-blue-600 text-white shadow-xl shadow-blue-900/40 scale-110 border border-blue-400/20'
                                : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10 hover:text-white hover:scale-105'
                                }`}
                        >
                            {page}
                        </button>
                    ))}
                </div>
            )}

            {isEditFormOpen && (
                <UserForm
                    user={editingUser}
                    onSubmit={handleUpdateUser}
                    onCancel={handleCloseEditForm}
                    allowedRoles={allowedRoles}
                    availableTenants={tenants}
                />
            )}

            <InviteModal
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                onSuccess={() => {
                    refresh();
                    // Note: Invited users won't show up in UserList instantly if UserList only shows active Users, 
                    // not Invites. The request implied managing users. 
                    // Ideally we should list Pending Invites too, but that's an extra feature 
                    // (List WorkspaceInvites).
                    // For now, refreshing users is correct for standard flow if auto-accepted or testing.
                    // But in reality, invites are pending.
                    // The user didn't explicitly ask for an "Invites List" UI, but it's implied for full management.
                    // I'll stick to what was asked: Migrate functionality + Update Button.
                }}
            />

            {/* AlertDialog de Confirmação */}
            <AlertDialog.Root open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-in fade-in duration-300" />
                    <AlertDialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] max-w-md z-[101] animate-in zoom-in-95 duration-300 focus:outline-none">
                        <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 rounded-[2.5rem] border border-white/10 shadow-2xl backdrop-blur-3xl relative overflow-hidden group">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-50" />
                            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-colors duration-500" />
                            
                            <div className="relative">
                                <AlertDialog.Title className="text-2xl font-bold text-white mb-4 tracking-tight font-oswald uppercase">
                                    {confirmDialog.title}
                                </AlertDialog.Title>
                                
                                <AlertDialog.Description className="text-slate-300 mb-8 leading-relaxed text-sm">
                                    {confirmDialog.message}
                                </AlertDialog.Description>

                                <div className="flex justify-end gap-4">
                                    <AlertDialog.Cancel asChild>
                                        <button className="px-6 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 transition-all duration-300">
                                            Cancelar
                                        </button>
                                    </AlertDialog.Cancel>
                                    
                                    <AlertDialog.Action asChild>
                                        <button
                                            onClick={() => {
                                                confirmDialog.onConfirm();
                                                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                                            }}
                                            className={`px-8 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${
                                                confirmDialog.type === 'danger' 
                                                    ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 shadow-red-500/20' 
                                                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-500/20'
                                            }`}
                                        >
                                            {confirmDialog.type === 'danger' ? 'Confirmar Exclusão' : 'Confirmar'}
                                        </button>
                                    </AlertDialog.Action>
                                </div>
                            </div>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </div>
    );
}
